/** Admin — Gestione Utenti Comunali (Sprint 2). Tenant enforced server-side. */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, UserPlus, KeyRound, Ban, RotateCcw, Pencil, ShieldAlert } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
        Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
        Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
        Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
        listUsers, createUser, updateUser, suspendUser, reactivateUser, resetUserPassword,
} from '@/services/api/users';
import { getConfig } from '@/services/api/config';
import { formatApiError } from '@/services/api/client';
import { useAuth } from '@/app/providers/AuthProvider';
import { USERS } from '@/constants/testIds';

const ROLE_LABELS = {
        municipality_admin: 'Amministratore',
        municipality_operator: 'Operatore',
        local_business: 'Attività Locale',
};

const EMPTY_FORM = { id: null, full_name: '', email: '', role: 'municipality_operator' };

export default function UsersPage() {
        const qc = useQueryClient();
        const { user: me } = useAuth();
        const [search, setSearch] = useState('');
        const [roleFilter, setRoleFilter] = useState('all');
        const [statusFilter, setStatusFilter] = useState('all');
        const [form, setForm] = useState(null);
        const [secret, setSecret] = useState(null); // { email, temporary_password }

        const { data: config } = useQuery({ queryKey: ['config'], queryFn: getConfig });
        const localBizEnabled = config?.operational?.local_business_enabled ?? true;

        const params = {};
        if (search) params.search = search;
        if (roleFilter !== 'all') params.role = roleFilter;
        if (statusFilter !== 'all') params.status = statusFilter;

        const { data, isLoading } = useQuery({
                queryKey: ['users', params],
                queryFn: () => listUsers(params),
        });

        const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });
        const onErr = (e) => toast.error(formatApiError(e.response?.data?.detail));

        const createMut = useMutation({
                mutationFn: (body) => createUser(body),
                onSuccess: (u) => {
                        setForm(null);
                        setSecret({ email: u.email, temporary_password: u.temporary_password });
                        invalidate();
                },
                onError: onErr,
        });
        const updateMut = useMutation({
                mutationFn: ({ id, body }) => updateUser(id, body),
                onSuccess: () => { setForm(null); toast.success('Utente aggiornato'); invalidate(); },
                onError: onErr,
        });
        const suspendMut = useMutation({ mutationFn: suspendUser, onSuccess: () => { toast.success('Utente sospeso'); invalidate(); }, onError: onErr });
        const reactivateMut = useMutation({ mutationFn: reactivateUser, onSuccess: () => { toast.success('Utente riattivato'); invalidate(); }, onError: onErr });
        const resetMut = useMutation({
                mutationFn: resetUserPassword,
                onSuccess: (u) => setSecret({ email: u.email, temporary_password: u.temporary_password }),
                onError: onErr,
        });

        const openCreate = () => setForm({ ...EMPTY_FORM });
        const openEdit = (u) => setForm({ id: u.id, full_name: u.full_name, email: u.email, role: u.role });

        const submit = () => {
                if (form.id) {
                        updateMut.mutate({ id: form.id, body: { full_name: form.full_name, email: form.email, role: form.role } });
                } else {
                        createMut.mutate({ full_name: form.full_name, email: form.email, role: form.role });
                }
        };

        const roleOptions = Object.keys(ROLE_LABELS).filter((r) => r !== 'local_business' || localBizEnabled);
        const items = data?.items ?? [];
        const saving = createMut.isPending || updateMut.isPending;

        return (
                <div data-testid={USERS.page} className="mx-auto max-w-5xl space-y-6">
                        <div className="flex items-center justify-between">
                                <div>
                                        <h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Poppins', sans-serif" }}>Utenti</h1>
                                        <p className="text-sm text-slate-400">Gestisci gli utenti del tuo Comune</p>
                                </div>
                                <Button data-testid={USERS.createButton} onClick={openCreate}
                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                        <UserPlus size={16} className="mr-1" /> Nuovo utente
                                </Button>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                                <Input data-testid={USERS.searchInput} placeholder="Cerca per nome o email…"
                                        value={search} onChange={(e) => setSearch(e.target.value)}
                                        className="border-slate-700 bg-slate-950/50 text-slate-100" />
                                <Select value={roleFilter} onValueChange={setRoleFilter}>
                                        <SelectTrigger data-testid={USERS.roleFilter} className="border-slate-700 bg-slate-950/50 text-slate-100">
                                                <SelectValue placeholder="Ruolo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                                <SelectItem value="all">Tutti i ruoli</SelectItem>
                                                {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                        </SelectContent>
                                </Select>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                        <SelectTrigger data-testid={USERS.statusFilter} className="border-slate-700 bg-slate-950/50 text-slate-100">
                                                <SelectValue placeholder="Stato" />
                                        </SelectTrigger>
                                        <SelectContent>
                                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                                <SelectItem value="active">Attivo</SelectItem>
                                                <SelectItem value="suspended">Sospeso</SelectItem>
                                        </SelectContent>
                                </Select>
                        </div>

                        {!localBizEnabled && (
                                <p data-testid={USERS.localBusinessDisabledHint} className="flex items-center gap-2 text-xs text-amber-400/80">
                                        <ShieldAlert size={13} /> La gestione delle attività locali è disabilitata per questo Comune.
                                </p>
                        )}

                        <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
                                {isLoading ? (
                                        <div data-testid={USERS.loading} className="flex items-center gap-2 p-6 text-slate-400">
                                                <Loader2 className="animate-spin" size={16} /> Caricamento utenti…
                                        </div>
                                ) : (
                                        <Table data-testid={USERS.table}>
                                                <TableHeader>
                                                        <TableRow className="border-slate-800 hover:bg-transparent">
                                                                <TableHead className="text-slate-400">Nome</TableHead>
                                                                <TableHead className="text-slate-400">Email</TableHead>
                                                                <TableHead className="text-slate-400">Ruolo</TableHead>
                                                                <TableHead className="text-slate-400">Stato</TableHead>
                                                                <TableHead className="text-right text-slate-400">Azioni</TableHead>
                                                        </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                        {items.length === 0 && (
                                                                <TableRow className="border-slate-800"><TableCell colSpan={5} className="py-8 text-center text-slate-500">Nessun utente trovato</TableCell></TableRow>
                                                        )}
                                                        {items.map((u) => (
                                                                <TableRow key={u.id} data-testid={`${USERS.row}-${u.id}`} className="border-slate-800">
                                                                        <TableCell className="font-medium text-slate-100">
                                                                                {u.full_name}
                                                                                {u.must_change_password && <span className="ml-2 text-[10px] text-amber-400">(reset in attesa)</span>}
                                                                        </TableCell>
                                                                        <TableCell className="text-slate-300">{u.email}</TableCell>
                                                                        <TableCell><Badge variant="outline" className="border-slate-700 text-slate-300">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                                                                        <TableCell>
                                                                                <Badge className={u.status === 'active' ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/15 text-rose-300'}>
                                                                                        {u.status === 'active' ? 'Attivo' : 'Sospeso'}
                                                                                </Badge>
                                                                        </TableCell>
                                                                        <TableCell className="text-right">
                                                                                <div className="flex justify-end gap-1">
                                                                                        <Button size="icon" variant="ghost" title="Modifica" data-testid={`${USERS.editButton}-${u.id}`}
                                                                                                onClick={() => openEdit(u)} className="h-8 w-8 text-slate-400 hover:text-slate-100"><Pencil size={15} /></Button>
                                                                                        <Button size="icon" variant="ghost" title="Reset password" data-testid={`${USERS.resetButton}-${u.id}`}
                                                                                                onClick={() => resetMut.mutate(u.id)} className="h-8 w-8 text-slate-400 hover:text-emerald-300"><KeyRound size={15} /></Button>
                                                                                        {u.status === 'active' ? (
                                                                                                u.id !== me?.id && (
                                                                                                        <Button size="icon" variant="ghost" title="Sospendi" data-testid={`${USERS.suspendButton}-${u.id}`}
                                                                                                                onClick={() => suspendMut.mutate(u.id)} className="h-8 w-8 text-slate-400 hover:text-rose-300"><Ban size={15} /></Button>
                                                                                                )
                                                                                        ) : (
                                                                                                <Button size="icon" variant="ghost" title="Riattiva" data-testid={`${USERS.reactivateButton}-${u.id}`}
                                                                                                        onClick={() => reactivateMut.mutate(u.id)} className="h-8 w-8 text-slate-400 hover:text-emerald-300"><RotateCcw size={15} /></Button>
                                                                                        )}
                                                                                </div>
                                                                        </TableCell>
                                                                </TableRow>
                                                        ))}
                                                </TableBody>
                                        </Table>
                                )}
                        </div>

                        {/* Create / Edit dialog */}
                        <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
                                <DialogContent data-testid={USERS.dialog} className="border-slate-800 bg-slate-950 text-slate-100">
                                        <DialogHeader><DialogTitle>{form?.id ? 'Modifica utente' : 'Nuovo utente'}</DialogTitle>
                                                <DialogDescription className="sr-only">Modulo di creazione o modifica di un utente del Comune.</DialogDescription>
                                        </DialogHeader>
                                        {form && (
                                                <div className="space-y-4">
                                                        <div>
                                                                <Label className="text-xs text-slate-400">Nome completo</Label>
                                                                <Input data-testid={USERS.fullNameInput} value={form.full_name}
                                                                        onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                                                                        className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                        </div>
                                                        <div>
                                                                <Label className="text-xs text-slate-400">Email</Label>
                                                                <Input data-testid={USERS.emailInput} type="email" value={form.email}
                                                                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                                                        className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                        </div>
                                                        <div>
                                                                <Label className="text-xs text-slate-400">Ruolo</Label>
                                                                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                                                                        <SelectTrigger data-testid={USERS.roleSelect} className="mt-1 border-slate-700 bg-slate-900 text-slate-100"><SelectValue /></SelectTrigger>
                                                                        <SelectContent>
                                                                                {roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                                                                        </SelectContent>
                                                                </Select>
                                                        </div>
                                                        {!form.id && (
                                                                <p className="text-xs text-slate-500">Verrà generata una password temporanea, mostrata una sola volta. L'utente dovrà cambiarla al primo accesso.</p>
                                                        )}
                                                </div>
                                        )}
                                        <DialogFooter>
                                                <Button variant="ghost" onClick={() => setForm(null)} className="text-slate-400">Annulla</Button>
                                                <Button data-testid={USERS.submitButton} onClick={submit} disabled={saving}
                                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                                        {saving && <Loader2 size={15} className="mr-1 animate-spin" />} {form?.id ? 'Salva' : 'Crea utente'}
                                                </Button>
                                        </DialogFooter>
                                </DialogContent>
                        </Dialog>

                        {/* Temporary password (shown once) */}
                        <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
                                <DialogContent data-testid={USERS.secretDialog} className="border-slate-800 bg-slate-950 text-slate-100">
                                        <DialogHeader><DialogTitle>Password temporanea</DialogTitle>
                                                <DialogDescription className="sr-only">Password temporanea generata, visibile una sola volta.</DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-3">
                                                <p className="text-sm text-slate-400">Comunica questa password a <span className="text-slate-200">{secret?.email}</span>. Non sarà più visibile dopo la chiusura.</p>
                                                <code data-testid={USERS.secretValue} className="block rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-center text-lg font-bold tracking-wider text-emerald-300">
                                                        {secret?.temporary_password}
                                                </code>
                                        </div>
                                        <DialogFooter>
                                                <Button data-testid={USERS.secretCloseButton} onClick={() => setSecret(null)}
                                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">Ho copiato la password</Button>
                                        </DialogFooter>
                                </DialogContent>
                        </Dialog>
                </div>
        );
}
